import { useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../ui-elements/Loading';
import Button from '../../layout/Button';
import { OPERATOR, TENANT_ADMIN, MARKETING } from '../../../constants/roles/roles';
import AuthContext from '../../../contexts/auth/AuthContext';
import ProduceTypeContext from '../../../contexts/management/produceTypes/ProduceTypeContext';
import ConfirmEditModal from './ConfirmEditModal';
import useFarmerAppPolicy from '../../../customHooks/useFarmerAppPolicy';
import ProcessContext from '../../../contexts/process/ProcessContext';
import { HELP_DESK_LINK } from '../../../constants/helpDesk';
import ConfirmDeleteModal from '../seedTypes/ConfirmDeleteModal';

const Card = ({ produceType }) => {
  const { accessibleSeedTypes} = useContext(AuthContext);
  const { notifyError, notifySuccess } = useContext(ProcessContext);
  const { allProduceTypes, deleteProduceType, getManyProduceTypes, loading } = useContext(ProduceTypeContext);

  const [showConfirmEditModal, setShowConfirmEditModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [seedType, setSeedType] = useState(null);

  const navigate = useNavigate();
  const tenantAdminPolicy = useFarmerAppPolicy([TENANT_ADMIN]);
  const operatorPolicy = useFarmerAppPolicy([TENANT_ADMIN, OPERATOR, MARKETING]);

  useEffect(() => {
    const seedType = accessibleSeedTypes?.find((st) => st?.id === produceType?.seedTypeId);
    setSeedType(seedType || null);
  }, [produceType, accessibleSeedTypes]);

  const getSeedTypeNameAndNumber = () =>
    seedType ? `${seedType.name} (Nº ${seedType.number})` : '';

  const handleDelete = (event) => {
    event?.stopPropagation();
    setShowConfirmDeleteModal(true);
  };

  const onCancelConfirmDelete = () => {
    setShowConfirmDeleteModal(false);
  };

  const confirmDelete = async () => {
    try {
      await deleteProduceType(produceType.id);
      notifySuccess('Successfully deleted produce type.');
      await getManyProduceTypes();
    } catch (error) {
      console.error("Error while deleting produce type.", error);
      if (error?.internalStatusCode === 404) {
        notifyError(
          <>We have encountered an issue while deleting the produce type. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team {HELP_DESK_LINK}.</>
        );
      } else {
        notifyError(
          <>We have encountered an issue on our side while deleting the produce type. Please contact the support team {HELP_DESK_LINK}.</>
        );
      }
    } finally {
      setShowConfirmDeleteModal(false);
    }
  };

  const handleDetail = () => {
    navigate(`/management/produce-types/${produceType.id}/details`);
  };

  const handleEdit = (event) => {
    event?.stopPropagation();
    setShowConfirmEditModal(true);
  };

  const onCancelConfirmEdit = () => {
    setShowConfirmEditModal(false);
  };

  const confirmEdit = useCallback(() => {
    navigate(`/management/produce-types/${produceType.id}/edit`);
  }, [navigate, produceType?.id]);

  return (allProduceTypes.length < 1 || (accessibleSeedTypes?.length ?? 0) < 1 || loading || !seedType)
    ? <Loading fullScreen={true} />
    : (
    <>
      <ConfirmEditModal
        show={showConfirmEditModal}
        onCancel={onCancelConfirmEdit}
        onConfirm={confirmEdit}
      />

      <ConfirmDeleteModal
        show={showConfirmDeleteModal}
        onCancel={onCancelConfirmDelete}
        onConfirm={confirmDelete}
        header={"DELETE PRODUCE TYPE"}
        message={
          <>
            Deleting this produce type will affect ALL produces, including those
            that have already left the container or are in the hands of consumers.
          </>
        }
      />

<div className="produce-type-card" onClick={handleDetail}>
        <div className="produce-type-card-header">
          <div className="produce-type-name-settings">
            <div className="produce-type-display produce-type-display--title" title={produceType?.marketingName ?? ""}>
              {produceType?.marketingName ?? ""}
            </div>
          </div>

          <div className="produce-type-card-number">N° {produceType?.number}</div>

          <div className="produce-type-classification-settings">
            <div className="produce-type-display" title={produceType?.classification ?? ""}>
              {produceType?.classification ?? ""}
            </div>
          </div>
        </div>

        <div className="produce-type-card-body">
          <div className="input-label">Seed Type</div>
          <div className="produce-type-property-paragraph" title={getSeedTypeNameAndNumber()}>
            {getSeedTypeNameAndNumber()}
        </div>
      </div>

        <div className="card-button produce-type-card-footer">
          <Button
            className="produce-type-card-btn"
            label={"Delete"}
            clickHandler={handleDelete}
            disabled={tenantAdminPolicy.isForbidden()}
          />
          <Button
            className="produce-type-card-btn"
            label={"Edit"}
            clickHandler={handleEdit}
            disabled={operatorPolicy.isForbidden()}
          />
        </div>
      </div>
    </>
  );
};

export default Card;
